import {
  StyledEditNutrientForm,
  EditNutrientFormHeader,
  EditNutrientFormTitle,
  EditNutrientDescription,
  StrongNutrientInfo,
  CaloriesError,
  EditNutrientBottomDescriptionBox,
  EditNutrientBottomDescription,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import { FlexBox } from "components/Common/FlexBox"
import ChevronLeftSmall from "components/Icons/ChevronLeftSmall"
import FlatMacroInput from "components/inputs/FlatMacroInput"
import useManualEditNutritionForm from "features/meal/hooks/useManualEditNutritionForm"
import { FoodType, MealType } from "model/food/types"
import { formatUnit } from "utils/formater"
import { INutrition } from "model/food/types/base.types"
import {
  isEdamaFood,
  hasIngredients,
} from "model/food/utils/typeGuards/foodTypeGuards"
import { IAIScanFoodOption } from "model/food/types/AIScan.types"

const InfoIcon = () => (
  <svg
    style={{ flexShrink: 0 }}
    xmlns="http://www.w3.org/2000/svg"
    width="34"
    height="34"
    viewBox="0 0 34 34"
    fill="none"
  >
    <rect width="34" height="34" rx="16.8367" fill="#EBEDF0" />
    <g clipPath="url(#clip0_40010686_75260)">
      <circle
        cx="17"
        cy="17"
        r="9"
        stroke="#00263A"
        strokeOpacity="0.32"
        strokeWidth="2"
      />
      <rect
        x="16"
        y="16"
        width="2"
        height="6"
        rx="1"
        fill="#00263A"
        fillOpacity="0.32"
      />
      <circle cx="17" cy="13" r="1" fill="#00263A" fillOpacity="0.32" />
    </g>
    <defs>
      <clipPath id="clip0_40010686_75260">
        <rect width="24" height="24" fill="white" transform="translate(5 5)" />
      </clipPath>
    </defs>
  </svg>
)

interface IEditNutrientFormProps {
  onClose: () => void
  meal: MealType | FoodType | IAIScanFoodOption
  onSubmit: (nutrition: INutrition) => void
}

const EditNutrientForm = (props: IEditNutrientFormProps) => {
  const { onClose, meal, onSubmit } = props
  const {
    calories,
    proteinGram,
    fatGram,
    carbsGram,
    isValuesChanged,
    isCaloriesError,
    onCaloriesChange,
    onProteinGramChange,
    onFatGramChange,
    onCarbsGramChange,
  } = useManualEditNutritionForm(meal)

  const showIngredientWarning = hasIngredients(meal) && !meal.isManualNutrition

  const handleBack = () => {
    if (isValuesChanged()) {
      const nutrition: INutrition = {
        calorie: calories!,
        protein: proteinGram ?? 0,
        fat: fatGram ?? 0,
        carbohydrate: carbsGram ?? 0,
        gram: null,
      }
      onSubmit(nutrition)
    }
    onClose()
  }

  return (
    <StyledEditNutrientForm>
      <EditNutrientFormHeader>
        <BackButton onClick={handleBack}>
          <ChevronLeftSmall />
        </BackButton>

        <EditNutrientFormTitle>Edit Nutrition</EditNutrientFormTitle>

        <BackButton></BackButton>
      </EditNutrientFormHeader>

      <EditNutrientDescription>
        You are editing portion for{" "}
        <StrongNutrientInfo>
          {meal.portion} {formatUnit(meal.unit, isEdamaFood(meal))}
        </StrongNutrientInfo>{" "}
        of <StrongNutrientInfo>{meal.name}</StrongNutrientInfo>
      </EditNutrientDescription>

      <FlexBox
        $direction="column"
        $gap={12}
        style={{ width: "100%", padding: "0 var(--app-horizontal-padding)" }}
      >
        <FlatMacroInput
          label="Calories"
          value={calories}
          onChange={onCaloriesChange}
          detailTrailing="cal"
          isError={isCaloriesError}
        />

        {isCaloriesError && (
          <CaloriesError>
            Calorie must be more than 0 and less than 10,000
          </CaloriesError>
        )}

        <FlatMacroInput
          label="Protein"
          value={proteinGram}
          onChange={onProteinGramChange}
          detailTrailing="g"
          decimalScale={1}
        />

        <FlatMacroInput
          label="Fat"
          value={fatGram}
          onChange={onFatGramChange}
          detailTrailing="g"
          decimalScale={1}
        />

        <FlatMacroInput
          label="Carbs"
          value={carbsGram}
          onChange={onCarbsGramChange}
          detailTrailing="g"
          decimalScale={1}
        />
      </FlexBox>

      {showIngredientWarning && (
        <EditNutrientBottomDescriptionBox>
          <InfoIcon />
          <EditNutrientBottomDescription>
            Calories are calculated from individual ingredients. Changing this
            will break that relationship.
          </EditNutrientBottomDescription>
        </EditNutrientBottomDescriptionBox>
      )}
    </StyledEditNutrientForm>
  )
}

export default EditNutrientForm
